import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Stack } from "react-bootstrap";
import Textarea from "../../../ui/Textarea";
import SelectFamily from "../../../ui/SelectFamily";
import ViewRecordings from "./ViewRecordings";
import TimeDuration from "../../../ui/TimeDuration";
import DatePicker from "../../../ui/DatePicker";
import { User } from "../../../../types/user";

interface StudentState {
  note?: any;
  family: User[];
  setUpdatedNote: any;
  setSaveIsEnabled: any;
  setUserToSave: any;
  myCase?: boolean;
  editNote?: boolean;
}

const StudentConnections = ({ note, family, setUpdatedNote, setSaveIsEnabled, setUserToSave, myCase, editNote = false }: StudentState) => {
  const mainRef = useRef();
  const [familyUser, setFamilyUser] = useState<string>("unassigned");
  const [visitHour, setVisitHour] = useState<number>(0);
  const [visitMin, setVisitMin] = useState<number>(0);
  const [summary, setSummary] = useState<string>("");
  const [wellBeing, setWellBeing] = useState<string>("");
  const [noteDate, setNoteDate] = useState<string>("");
  const [initialReadOnly, setInitialReadOnly] = useState<boolean>(true);

  const handleScroll = (ref: any) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
    }
  };

  useEffect(() => {
    handleScroll(mainRef);

    const realFamily = family.filter((member) => member.name !== "Unassigned");

    if (editNote) {
      setFamilyUser(note?.subject?.id + "|" + note?.subject?.type);
      const isDeleted = family.find((f) => f.id == note?.subject?.id)?.isDeleted;
      if (isDeleted === undefined) {
        setInitialReadOnly(!myCase);
      } else {
        setInitialReadOnly(!myCase || isDeleted);
      }
      setNoteDate(note?.fields?.date);
      setVisitHour(note.fields.visit_hour);
      setVisitMin(note.fields.visit_min);
      setSummary(note.fields.summary_info);
      setWellBeing(note.fields.wellbeing);
    } else {
      setNoteDate(new Date().toISOString().split("T")[0]);
      setInitialReadOnly(!myCase);
    }

    if (realFamily.length === 1 && !editNote) {
      setFamilyUser(realFamily[0].id + "|" + realFamily[0].type);
    }
  }, []);

  useEffect(() => {
    const parts = familyUser.split("|");

    const note = {
      notetype: "student",
      subject:
        familyUser !== "unassigned"
          ? {
              id: parts[0],
              type: parts[1],
            }
          : { id: 0 },
      fields: {
        date: noteDate,
        visit_hour: visitHour,
        visit_min: visitMin,
        summary_info: summary,
        wellbeing: wellBeing,
      },
    };

    setUpdatedNote(note);
  }, [noteDate, visitHour, visitMin, summary, wellBeing, familyUser, setUpdatedNote]);

  const updateFamilyUser = (e: any) => {
    setFamilyUser(e.target.value);
    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateSummary = (e: any) => {
    setSummary(e.target.value);
    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateWellBeing = (e: any) => {
    setWellBeing(e.target.value);
    if (noteDate) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    }
  };

  const updateDate = () => {
    setSaveIsEnabled(true);
    setUserToSave(true);
  };

  useEffect(() => {
    if (!noteDate) {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  }, [noteDate]);

  return (
    <Stack gap={3} className="mt-4">
      <Row ref={mainRef}>
        <SelectFamily
          family={family}
          familyUser={familyUser ? familyUser : "unassigned"}
          updateFamilyUser={updateFamilyUser}
          readOnly={initialReadOnly}
          defaultSelection="Unassigned"
        />
        <Col className="col-lg-4 col-md-6 col-sm-8">
          <DatePicker
            label="Date"
            currentValue={noteDate}
            setCurrentValue={setNoteDate}
            onUpdate={() => updateDate()}
            required={true}
            hasMaxDate={true}
            disabled={myCase ? false : true}
          />
        </Col>
      </Row>
      {note?.recordingsExist === true && <ViewRecordings note={note} editNote={editNote} myCase={myCase} />}
      <TimeDuration
        hours={visitHour}
        setHours={setVisitHour}
        minutes={visitMin}
        setMinutes={setVisitMin}
        setSaveIsEnabled={setSaveIsEnabled}
        setUserToSave={setUserToSave}
        disabled={myCase ? false : true}
      />
      <Row>
        <Col xs={12}>
          <Textarea
            label="Information Discussed"
            value={summary}
            onUpdate={(e: any) => updateSummary(e)}
            disabled={myCase ? false : true}
          />
        </Col>
      </Row>
      <Row className="mb-5">
        <Col xs={12}>
          <Textarea
            label="State of Student Well-Being"
            value={wellBeing}
            onUpdate={(e: any) => updateWellBeing(e)}
            disabled={myCase ? false : true}
          />
        </Col>
      </Row>
    </Stack>
  );
};

export default StudentConnections;
